import { types, type SnapshotIn } from 'mobx-state-tree';
import FSM from '../metamodel/fsm';
import { modelReference } from '../refHelper';
import FSMNode, { type IFSMNode } from './fsmnode';
import FSMLink, { type IFSMLink } from './fsmlink';
import type { IFSM } from '../metamodel/fsm';

/**
 * The FSMView instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof FSMView>`) because it is the FSM container:
 * `FSMNode` and `FSMLink` reach it back through `getParent(self, 2)`. Typing those
 * cross-getters against this interface lets TS resolve them via the interface
 * instead of re-inferring `typeof FSMView` (which embeds the child arrays and
 * loops back into each child's own initializer).
 */
export interface IFSMView {
  readonly fsm: IFSM;
  readonly nodes: IFSMNode[];
  readonly links: IFSMLink[];
  findLinkById(id: string): IFSMLink | undefined;
  findNodeById(id: string): IFSMNode | undefined;
  readonly topLeftCoordinates: { x: number; y: number };
  addNode(
    state: string,
    position?: SnapshotIn<typeof FSMNode>['position'],
    size?: SnapshotIn<typeof FSMNode>['size']
  ): IFSMNode;
  addLink(transition: string, vertices?: SnapshotIn<typeof FSMLink>['vertices']): IFSMLink;
  removeNode(stateId: string): void;
  removeLink(transitionId: string): void;
}

type FSMNodeSnap = SnapshotIn<typeof FSMNode>;
type FSMLinkSnap = SnapshotIn<typeof FSMLink>;

const FSMView = types
  .model('FSMView', {
    fsm: modelReference<typeof FSM>(FSM),
    // nodes: aka the visual counterpart of a state
    nodes: types.optional(types.array(FSMNode), () => []),
    // links: aka the visual counterpart of a transition
    links: types.optional(types.array(FSMLink), []),
  })
  .views((self) => ({
    // find the link that represents the given transition (by id)
    findLinkById(id: string) {
      return self.links.find((l) => l.transition.id === id);
    },
    // find the node that represents the given state (by id)
    findNodeById(id: string) {
      return self.nodes.find((n) => n.state.id === id);
    },
    // get the coordinates of the top left corner of bounding box of the view
    get topLeftCoordinates() {
      const x = Math.min(...self.nodes.map((n) => n.position.x));
      const y = Math.min(...self.nodes.map((n) => n.position.y));
      return {
        x,
        y,
      };
    },
  }))
  .actions((self) => ({
    // state is an id! We need this to be serializable to allow replay.
    addNode(state: string, position?: FSMNodeSnap['position'], size?: FSMNodeSnap['size']) {
      const node = FSMNode.create({
        state,
        position,
        size,
      } as FSMNodeSnap);
      self.nodes.push(node);
      return node;
    },
    // transition is an id! We need this to be serializable to allow replay.
    addLink(transition: string, vertices: FSMLinkSnap['vertices'] = []) {
      const link = FSMLink.create({
        transition,
        vertices,
      } as FSMLinkSnap);
      self.links.push(link);
      return link;
    },
    removeNode(stateId: string) {
      const node = self.findNodeById(stateId);
      self.nodes.remove(node as unknown as (typeof self.nodes)[number]);
    },
    removeLink(transitionId: string) {
      const link = self.findLinkById(transitionId);
      self.links.remove(link as unknown as (typeof self.links)[number]);
    },
  }));

export default FSMView;
